// TfL arrivals -> trains. TfL predicts arrivals per station, not per train,
// so this groups the predictions into trains (by vehicle id, or by station and
// direction when TfL sends a dummy id), works out which station each just
// left (findPrev), learns run times from what TfL itself predicts, and drops
// trips TfL publishes before they run. Everything after this is the core's:
// the same TrainPrediction that New York builds from its GTFS feeds.

import * as Core from '@tol/core/core';
import type { RunTime } from '@tol/core/core';
import { isGroupPrediction } from '@tol/core/feed';
import type { FeedCore } from '@tol/core/feed';
import type { Arrival, ComputedTrains, Network, NetworkLine, StationId, Stop, TrainPrediction } from '@tol/core/types';
import type { TflPrediction } from '../shared/tfl.ts';
import { elizabethRun, elizabethRunKey } from './elizabeth-handover.ts';

const { baseName, cleanName, either, findPrev, isRealVehicleId, normName, segTime } = Core;

type ArrivalDraft = { -readonly [K in keyof Arrival]: Arrival[K] };

function resolve(L: NetworkLine, naptanId: string | undefined, name: string | undefined): StationId | null {
  const hasId = naptanId !== undefined && naptanId !== '';
  if (hasId && L.ids.has(naptanId)) return naptanId;
  return (
    (hasId ? L.alias.get(naptanId) : undefined) ??
    L.alias.get('n:' + normName(name)) ??
    L.alias.get('b:' + baseName(name)) ??
    null
  );
}

// Which station did this train most likely just leave?
// Median of what TfL itself predicts for a->b right now, across every train
// that has both stops coming up. Falls back to the heuristic.
function learnRunTimes(net: Network, groups: ReadonlyMap<string, readonly Arrival[]>): RunTime {
  const obs = new Map<string, number[]>();
  for (const rs of groups.values()) {
    const [first] = rs;
    if (!first) continue;
    const L = net.lines[first.lineId];
    if (!L) continue;
    let prev: Arrival | undefined;
    for (const cur of rs) {
      if (prev) {
        const a = prev.sid;
        const b = cur.sid;
        const adj = L.adj.get(a);
        if (adj?.has(b) === true) {
          const gap = (cur.eta - prev.eta) / 1000;
          const est = segTime(net, a, b, first.lineId);
          // Skip a dwell at the origin and bad data.
          if (!(gap < est * 0.4 || gap > est * 2.5)) {
            const k = first.lineId + '|' + a + '|' + b;
            let v = obs.get(k);
            if (!v) obs.set(k, (v = []));
            v.push(gap);
          }
        }
      }
      prev = cur;
    }
  }
  const med = new Map<string, number>();
  for (const [k, v] of obs) {
    v.sort((x, y) => x - y);
    const m = v[v.length >> 1];
    if (m !== undefined) med.set(k, m);
  }
  // Every learned gap is positive (see the filter above), so ?? acts as ||.
  return (lineId, a, b) => med.get(lineId + '|' + a + '|' + b) ?? segTime(net, a, b, lineId);
}

// Elizabeth line and Overground vehicle ids are trip ids, and TfL publishes
// trips long before they run. Those show up as a first stop "due now" at the
// origin followed by a big gap, or a first stop far in the future. Neither
// is a train on the network yet, so skip it (null). Trains really waiting at
// their first stop come back as a copy with P null and waiting set.
// s: show a waiting train if it leaves within 10 min
const START_SOON = 600;
// s: tolerated excess over the estimated run time
const MAX_LATE = 240;
function onTheMove(t: TrainPrediction, stops: readonly Stop[], now: number, runTime: RunTime): TrainPrediction | null {
  const tts = (t.eta - now) / 1000;
  const n2 = stops[1];
  if (n2) {
    const run = runTime(t.lineId, t.N, n2.sid);
    const gap = (n2.eta - t.eta) / 1000;
    if (gap > run + 180) {
      const departs = (n2.eta - now) / 1000 - run;
      if (departs > START_SOON) return null;
      // Sitting at t.N until it leaves.
      return { ...t, P: null, segT: 0, waiting: true };
    }
  }
  if (t.P !== null) return tts <= t.segT + MAX_LATE ? t : null;
  return tts <= START_SOON ? t : null;
}

function adjacent(line: NetworkLine | undefined, a: Arrival | undefined, b: Arrival | undefined): boolean {
  return a !== undefined && b !== undefined && line?.adj.get(a.sid)?.has(b.sid) === true;
}

// TfL lists some Elizabeth line calls twice, at the same time and platform,
// under both of a station's ids (Paddington 910GPADTLL and 910GPADTON,
// Liverpool Street 910GLIVSTLL and 910GLIVST). Both are on the route, but only
// one connects to the train's other stops, and the two tie when sorted by
// time, so which came first changed from poll to poll and the train snapped
// between them. Keep one stop per call: the one that connects to the stop
// before or after it. stops: one train's, soonest first.
function oneStationPerCall(net: Network, stops: readonly Arrival[]): Arrival[] {
  const out: Arrival[] = [];
  for (let i = 0; i < stops.length;) {
    let j = i + 1;
    while (j < stops.length && stops[j]?.eta === stops[i]?.eta) j++;
    const call = stops.slice(i, j);
    const [first] = call;
    if (first && call.length > 1) {
      const line = net.lines[first.lineId];
      const before = out.at(-1);
      // The next call may list two ids as well, and either counts.
      let k = j + 1;
      while (k < stops.length && stops[k]?.eta === stops[j]?.eta) k++;
      const after = stops.slice(j, k);
      const joinsAfter = (s: Arrival): boolean => after.some((a) => adjacent(line, s, a));
      // Candidates by id, so ties don't depend on the response's order.
      const byId = call.toSorted((a, b) => (a.sid < b.sid ? -1 : a.sid > b.sid ? 1 : 0));
      const joined =
        byId.find((s) => adjacent(line, before, s) && joinsAfter(s)) ??
        byId.find((s) => adjacent(line, before, s) || joinsAfter(s));
      // Otherwise the lowest id, so the choice at least doesn't change per poll.
      out.push(joined ?? byId[0] ?? first);
    } else if (first) {
      out.push(first);
    }
    i = j;
  }
  return out;
}

// predictions: raw TfL Prediction objects. now: ms since epoch (server-corrected).
function computeTrains(net: Network, predictions: readonly (TflPrediction | null)[], now: number): ComputedTrains {
  const groups = new Map<string, ArrivalDraft[]>();
  const loose = new Map<string, ArrivalDraft[]>();
  const byStation = new Map<StationId, ArrivalDraft[]>();

  for (const p of predictions) {
    if (!p || typeof p !== 'object' || p.lineId === undefined) continue;
    const L = net.lines[p.lineId];
    if (!L) continue;
    const sid = resolve(L, p.naptanId, p.stationName);
    if (sid === null || sid === '') continue;
    const eta = Date.parse(p.expectedArrival ?? '');
    if (!Number.isFinite(eta) || eta < now - 30000) continue;
    const destination = cleanName(either(p.destinationName, p.towards ?? ''));
    const hasDestination = !/^(check front of train|unknown|not known)$/iu.test(destination);
    const r: ArrivalDraft = {
      sid,
      eta,
      lineId: p.lineId,
      dir: (p.direction ?? '').toLowerCase(),
      loc: p.currentLocation ?? '',
      dest: hasDestination ? destination : '',
      towards: p.towards ?? '',
      platform: p.platformName ?? '',
      vid: (p.vehicleId ?? '').trim(),
    };
    let list = byStation.get(sid);
    if (!list) byStation.set(sid, (list = []));
    list.push(r);

    let bucket: Map<string, ArrivalDraft[]>;
    let k: string;
    if (isRealVehicleId(r.vid)) {
      r.destKey = hasDestination ? either(p.destinationNaptanId, r.dest) : '';
      bucket = groups;
      const run = elizabethRun(p);
      k = p.lineId + '|' + (run === null ? r.vid : 'nr:' + elizabethRunKey(run)) + '|' + r.destKey;
    } else {
      bucket = loose;
      k = p.lineId + '|' + sid + '|' + either(r.dir, r.platform);
    }
    let g = bucket.get(k);
    if (!g) bucket.set(k, (g = []));
    g.push(r);
  }

  // Each vehicle's calls, one station per call: learned run times come from
  // these too, so a doubled call can't change them.
  const calls = new Map<string, Arrival[]>();
  for (const [key, rs] of groups) {
    rs.sort((a, b) => a.eta - b.eta);
    const seen = new Set<StationId>();
    const unique: Arrival[] = [];
    for (const r of rs) {
      if (!seen.has(r.sid)) {
        seen.add(r.sid);
        unique.push(r);
      }
    }
    calls.set(key, oneStationPerCall(net, unique));
  }
  const runTime = learnRunTimes(net, calls);

  const trains: TrainPrediction[] = [];
  const make = (key: string, stops: readonly Arrival[]): TrainPrediction | null => {
    const [n, n2] = stops;
    if (!n) return null;
    const L = net.lines[n.lineId];
    if (!L) return null;
    const P = findPrev(net, L, n.sid, n2?.sid, n.dir, n.loc);
    return {
      key,
      lineId: n.lineId,
      P,
      N: n.sid,
      eta: n.eta,
      // The learned gap between arrivals includes the dwell at P.
      segT: P === null || P === '' ? 0 : Core.runPart(runTime(n.lineId, P, n.sid)),
      dest: n.dest,
      towards: n.towards,
      loc: n.loc,
      platform: n.platform,
      vid: n.vid,
      waiting: false,
      stops: stops.slice(0, 8).map((s) => ({ sid: s.sid, eta: s.eta })),
    };
  };

  // TfL often adds predictions with no destination ("Check Front of Train")
  // for a train's next trip after it reverses. When the same vehicle also has
  // a proper trip, those are not a second train, so drop them.
  const withDest = new Set<string>();
  for (const [r] of groups.values()) {
    if (r?.destKey !== undefined && r.destKey !== '') withDest.add(r.lineId + '|' + r.vid);
  }
  for (const [key, [r]] of groups) {
    if (r && (r.destKey === undefined || r.destKey === '') && withDest.has(r.lineId + '|' + r.vid)) {
      groups.delete(key);
    }
  }

  const vehicles = new Map<string, number>();
  for (const key of groups.keys()) {
    const stops = calls.get(key) ?? [];
    const made = make(key, stops);
    const t = made && onTheMove(made, stops, now, runTime);
    if (!t) continue;
    const vehicle = t.lineId + '|' + t.vid;
    const index = vehicles.get(vehicle);
    const previous = index === undefined ? undefined : trains[index];
    if (previous !== undefined && index !== undefined) {
      const next = t.stops?.find((s) => s.eta >= now)?.eta;
      const previousNext = previous.stops?.find((s) => s.eta >= now)?.eta;
      const phase = next === undefined ? 2 : t.waiting ? 1 : 0;
      const previousPhase = previousNext === undefined ? 2 : previous.waiting ? 1 : 0;
      const eta = next ?? -(t.stops?.at(-1)?.eta ?? t.eta);
      const previousEta = previousNext ?? -(previous.stops?.at(-1)?.eta ?? previous.eta);
      if (
        phase < previousPhase ||
        (phase === previousPhase && (eta < previousEta || (eta === previousEta && t.key < previous.key)))
      )
        trains[index] = t;
      continue;
    }
    vehicles.set(vehicle, trains.length);
    trains.push(t);
  }

  // No vehicle id: keep only the first train approaching each station per
  // direction, and only if it is close enough to be on the segment before it.
  for (const [key, rs] of loose) {
    rs.sort((a, b) => a.eta - b.eta);
    const t = make('x|' + key, rs.slice(0, 1));
    if (!t || t.P === null || t.P === '') continue;
    if ((t.eta - now) / 1000 > runTime(t.lineId, t.P, t.N) + 30) continue;
    trains.push(t);
  }

  for (const list of byStation.values()) list.sort((a, b) => a.eta - b.eta);
  return { trains, byStation };
}

// What the poller (core's feed.ts) needs from TfL's arrivals.
const TFL_FEED: FeedCore<TflPrediction> = {
  isPrediction: (p, group): p is TflPrediction => isGroupPrediction(p, group),
  computeTrains,
  mergeTrains: (trains, computed, lines, now, retired) => {
    const keys = new Map(computed.filter((t) => isRealVehicleId(t.vid)).map((t) => [t.lineId + '|' + t.vid, t.key]));
    for (const t of trains.values()) {
      const key = keys.get(t.pred.lineId + '|' + t.pred.vid);
      if (key === undefined || key === t.key) continue;
      t.render.world = undefined;
      t.render.tail = null;
    }
    Core.mergeTrains(trains, computed, lines, now, retired);
  },
};

export { TFL_FEED, computeTrains, oneStationPerCall };
